import type {ReactNode} from 'react'
import {EyeIcon, CommentDiscussionIcon} from '@primer/octicons-react'
import type {
  ReviewRequestsData,
  CompletedReview,
  PendingReviewRequest,
} from '../../../page-data/payloads/review-requests'
import {ActionItem} from './ActionItem'
import styles from './ActionItems.module.css'
import {CompletedItemsDisclosure, type CompletedItem} from './CompletedItems'
import {pullRequestsChangesRoute} from '../../../routes/files-changed-routes'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {pullRequestsOverviewRoute} from '../../../routes/overview-route'
import {DockedPanel} from '../../../contexts/DockedPanelContext'

interface ReviewerActionsProps {
  reviewers: ReviewRequestsData
  viewerLogin: string
  isReviewer: boolean
}

export function ReviewerActions({reviewers, viewerLogin, isReviewer}: ReviewerActionsProps) {
  const viewerPending = findViewerPendingRequest(reviewers, viewerLogin)
  const viewerReview = findViewerCompletedReview(reviewers, viewerLogin)

  const items: ReactNode[] = []
  const completedItems: CompletedItem[] = []
  if (!isReviewer) {
    items.push(<ReviewNotRequestedItem key="not-requested" />)
  }

  if (viewerPending) {
    items.push(<ReviewRequestedItem key="requested" request={viewerPending} />)
  }

  if (viewerReview) {
    const resolvedCount = viewerReview.threadCounts.resolved
    const unresolvedCount = viewerReview.threadCounts.total - viewerReview.threadCounts.resolved
    if (unresolvedCount > 0) {
      items.push(
        <CommentsItem
          key="unresolved"
          commentType={'unresolved'}
          review={viewerReview}
          commentCount={unresolvedCount}
        />,
      )
    }
    if (resolvedCount > 0) {
      completedItems.push({
        label: `${resolvedCount} of your comment${resolvedCount === 1 ? '' : 's'} resolved`,
      })
    }

    if (viewerReview.state === 'approved') {
      completedItems.push({label: 'You approved this pull request'})
    } else if (viewerReview.state === 'changes_requested') {
      completedItems.push({label: 'You requested changes'})
    } else if (viewerReview.state === 'commented') {
      completedItems.push({label: 'You left a review'})
    }
  }

  if (items.length === 0) {
    items.push(<ActionItem key="no-action-items" leadingIcon={EyeIcon} title={`No action items for @${viewerLogin}`} />)
  }

  return (
    <>
      {items.length > 0 && (
        // eslint-disable-next-line jsx-a11y/no-redundant-roles
        <ul role="list" className={styles.actionItemList}>
          {items}
        </ul>
      )}
      <CompletedItemsDisclosure items={completedItems} />
    </>
  )
}

function ReviewNotRequestedItem() {
  const {owner, repo, pr_number} = useRouteParams(pullRequestsOverviewRoute)

  const viewChangesPath = pullRequestsChangesRoute.generatePath({owner, repo, pr_number})

  return (
    <ActionItem
      leadingIcon={EyeIcon}
      action={{
        linkTo: viewChangesPath,
        label: 'View changes',
        analyticsPayload: {section: 'reviewer_actions', context: 'review_not_requested'},
      }}
      title={`You have not reviewed this pull request yet`}
    />
  )
}

function ReviewRequestedItem({request, reRequest}: {request: PendingReviewRequest; reRequest?: boolean}) {
  const {owner, repo, pr_number} = useRouteParams(pullRequestsOverviewRoute)
  const isCodeOwner = request.isCodeOwner || request.isCodeOwnerFromDelegation
  const delegatedTeamName = request.delegatedFromTeam?.displayLogin
  const reRequestText = reRequest ? 're-' : ''

  let message = undefined
  if (isCodeOwner && delegatedTeamName) {
    message = `Assigned from ${delegatedTeamName}.`
  } else if (isCodeOwner) {
    message = 'Requested as a code owner.'
  } else if (delegatedTeamName) {
    message = `Requested on behalf of ${delegatedTeamName}.`
  }

  const viewChangesPath = pullRequestsChangesRoute.generatePath({owner, repo, pr_number})

  return (
    <ActionItem
      leadingIcon={EyeIcon}
      action={{
        linkTo: viewChangesPath,
        label: 'View changes',
        analyticsPayload: {section: 'reviewer_actions', context: 'review_requested'},
      }}
      title={`Your review has been ${reRequestText}requested`}
      description={message}
    />
  )
}

function CommentsItem({
  commentType,
  review,
  commentCount,
}: {
  commentType: string
  review: CompletedReview
  commentCount: number
}) {
  const resolvedFilter = commentType === 'resolved' ? ' is:resolved' : ' is:unresolved'
  const filterText = `review-id:${review.id}${resolvedFilter}`.trim()

  const commentText =
    commentCount === 1
      ? `${commentCount} of your comments is ${commentType}.`
      : `${commentCount} of your comments are ${commentType}.`

  return (
    <ActionItem
      leadingIcon={CommentDiscussionIcon}
      title={commentText}
      description={commentType === 'unresolved' ? 'You may still be waiting on others to take action.' : ''}
      action={{
        panel: DockedPanel.Comments,
        label: `View ${commentType}`,
        metadata: {
          filterText,
          hideResolved: commentType !== 'resolved',
          viewMode: 'review-comments',
        },
        analyticsPayload: {section: 'reviewer_actions', context: 'comments', comment_type: commentType},
      }}
    />
  )
}

function findViewerPendingRequest(
  reviewers: ReviewRequestsData,
  viewerLogin: string,
): PendingReviewRequest | undefined {
  return reviewers.pendingReviewRequests?.find(
    req => req.reviewer.displayLogin.toLowerCase() === viewerLogin.toLowerCase(),
  )
}

function findViewerCompletedReview(reviewers: ReviewRequestsData, viewerLogin: string): CompletedReview | undefined {
  return reviewers.completedReviews?.find(rev => rev.reviewer.displayLogin.toLowerCase() === viewerLogin.toLowerCase())
}
